import React, { useEffect, useState } from 'react';

import ApprovalHistoryTable from 'Components/budget_change_requests/ApprovalHistoryTable';
import ApprovalStepper from 'Components/budget_change_requests/ApprovalStepper';
import RequesterExplanation from 'Components/budget_change_requests/RequesterExplanation';
import UncommittedImpactHeader from 'Components/procurement_plan/UncommittedImpactHeader';

import { OUTCOME_PENDING } from '../../myviews/queueTypes';
import CommitmentAttachments from '../purchase_order/CommitmentAttachments';
import CommitmentContractSection from '../purchase_order/CommitmentContractSection';
import CommitmentDetailTable from '../purchase_order/CommitmentDetailTable';
import { amount } from '../purchase_order/commitmentFormat';
import CommitmentPositionCard from '../purchase_order/CommitmentPositionCard';
import CommitmentStatusLine from '../purchase_order/CommitmentStatusLine';
import PaymentMilestonesTable from '../purchase_order/PaymentMilestonesTable';
import ProcurementDeclarations from '../purchase_order/ProcurementDeclarations';

import RecipientsField from './RecipientsField';

/**
 * An Expenditure Approval or Direct Commitment as its approver decides it: what it
 * takes from the budget line and where that leaves each location, what it
 * commits, the contract and requisition behind it, and where it stands in its
 * chain.
 *
 * THE FIGURES ARE FETCHED ON OPEN, NOT READ OFF THE ROW. What is uncommitted on a
 * line moves whenever anything else commits against it, and this is the figure the
 * approver acts on.
 *
 * Once the order is decided the server leaves the live figures out: what it took
 * is history, but the balance it took it from has moved on since.
 */
export default function PurchaseOrderBody({ row, myViews, onExtrasChange, readOnly }) {
    const [detail, setDetail] = useState(null);
    const [failed, setFailed] = useState(false);

    const record = row?.record ?? {};

    useEffect(() => {
        if (!record.id) {
            return;
        }

        setDetail(null);
        setFailed(false);

        axios
            .get(myViews.fetch_purchase_order_url.replace('ORDER_ID', record.id))
            .then(({ data }) => {
                setDetail(data);

                // A cancelled or withdrawn order has nothing left to decide, and the
                // server still accepts a decision on one.
                if (data.unavailable) {
                    onExtrasChange?.((extras) => ({ ...extras, totalsUnavailable: true, recordUnavailable: true }));
                }
            })
            // Approve is withheld when the approver could not see what they would
            // commit. Reject needs none of it and stays.
            .catch(() => {
                setFailed(true);
                onExtrasChange?.((extras) => ({ ...extras, totalsUnavailable: true }));
            });
    }, [record.id]);

    if (failed) {
        return (
            <p className="modal__heading tw-text-red">
                {$t('The figures for this commitment could not be loaded, so it cannot be approved right now. You can still reject it.')}
            </p>
        );
    }

    if (!detail) {
        return <p className="modal__text">{$t('Loading…')}</p>;
    }

    if (detail.unavailable) {
        return <p className="modal__text">{$t('This record is no longer available.')}</p>;
    }

    const figures = detail.figures;
    const budgetLine = detail.budget_line ?? {};

    return (
        <div className="tw-flex tw-flex-col tw-gap-4">
            <CommitmentStatusLine detail={detail} pending={row?.outcome === OUTCOME_PENDING} />

            {figures ? (
                <>
                    <UncommittedImpactHeader
                        context={{
                            line: budgetLine.line_count > 1 ? $t('{{count}} budget lines', { count: budgetLine.line_count }) : budgetLine.name,
                            crumbs: budgetLine.path,
                        }}
                        lineUrl={budgetLine.url}
                        contextLabel={$t('Budget line')}
                        now={figures.uncommitted_now}
                        committing={figures.committing}
                        vikEnabled
                        captions={impactCaptions(figures)}
                    />
                    <CommitmentPositionCard rows={detail.position} />
                </>
            ) : (
                <p className="bc-modal-note tw-mb-0">{$t('This commitment has been decided; open the record for current figures.')}</p>
            )}

            <CommitmentDetailTable lines={detail.lines} total={detail.committing_total} />

            <CommitmentContractSection detail={detail} />

            {detail.explanation && <RequesterExplanation row={detail} />}

            {detail.procurement && detail.declarations && <ProcurementDeclarations procurement={detail.procurement} declarations={detail.declarations} />}

            {detail.milestones && <PaymentMilestonesTable milestones={detail.milestones} />}

            <CommitmentAttachments orderId={detail.id} attachments={detail.attachments} downloadUrl={myViews.download_purchase_order_attachment_url} />

            <div>
                <div className="bc-eyebrow">{$t('Approval progress')}</div>
                <ApprovalStepper approval={detail.approval} emptyLabel={$t('No approval levels are configured for this commitment.')} />
            </div>

            {/* The submission first, then each decision. ApprovalDetailModal leaves
                its generic approvers table out for purchase orders in favour of this. */}
            <ApprovalHistoryTable history={detail.history} />

            {!readOnly && (
                <RecipientsField
                    enabled={!!myViews.ea_approval_email_enabled}
                    requestor={record.owner || record.requested_by}
                    approvers={record.approval_history || record.approvers || []}
                    defaultRecipients={myViews.ea_approval_email_recipients}
                    onChange={(additionalRecipients) => onExtrasChange?.((extras) => ({ ...extras, additionalRecipients }))}
                />
            )}
        </div>
    );
}

const impactCaptions = (figures) => {
    const after = figures.uncommitted_after.cash + figures.uncommitted_after.vik;

    return {
        now: $t('of {{value}} latest estimate', { value: amount(figures.latest_estimate) }),
        committing: figures.committing.vik ? null : $t('cash, no VIK'),
        after: Math.abs(after) < 0.005 ? $t('line fully committed') : null,
    };
};
